'use client';

import { useEffect, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';

interface TickerConfig {
  enabled: boolean;
  messages: string[];
  background_color: string;
  text_color: string;
  speed: number;
  font_size: string;
  height: string;
  pause_on_hover: boolean;
  show_separator: boolean;
  separator_text: string;
  direction: 'left' | 'right';
}

export default function NewsTicker() {
  const pathname = usePathname();
  const [config, setConfig] = useState<TickerConfig | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Don't show ticker on admin pages
  const isAdminPage = pathname?.startsWith('/paneladminalibra');

  useEffect(() => {
    // Skip fetching if on admin page
    if (isAdminPage) return;

    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/ticker-config');
        const data = await response.json();

        if (data.success && data.data?.enabled) {
          // Parse messages if needed
          let messages = data.data.messages;
          if (typeof messages === 'string') {
            try {
              messages = JSON.parse(messages);
            } catch {
              messages = [];
            }
          }

          if (messages && messages.length > 0) {
            setConfig({
              ...data.data,
              messages
            });
          }
        }
      } catch (error) {
        console.error('Error fetching ticker config:', error);
      }
    };

    fetchConfig();
  }, [isAdminPage]);

  // Setup animation
  useEffect(() => {
    if (!config || !contentRef.current || !containerRef.current) return;

    const content = contentRef.current;
    const container = containerRef.current;
    let animation: Animation | null = null;
    let handleMouseEnter: (() => void) | null = null;
    let handleMouseLeave: (() => void) | null = null;

    // Pomiar scrollWidth i start animacji odłożone do requestAnimationFrame:
    // czytanie tej właściwości od razu w useEffect (tuż po zamontowaniu DOM przez
    // Reacta) wymuszało synchroniczny reflow (potwierdzone audytem Lighthouse
    // "forced-reflow-insight"); po odłożeniu o klatkę przeglądarka ma już policzony
    // layout ze swojego naturalnego cyklu renderowania.
    const rafId = requestAnimationFrame(() => {
      // Calculate how many times we need to repeat the content to fill the screen
      const contentWidth = content.scrollWidth / 2; // Divided by 2 because we already have 2 copies

      // Animation duration based on speed (pixels per second)
      const duration = (contentWidth / config.speed) * 1000;

      // Create animation
      const keyframes = config.direction === 'left'
        ? [
            { transform: 'translateX(0)' },
            { transform: `translateX(-${contentWidth}px)` }
          ]
        : [
            { transform: `translateX(-${contentWidth}px)` },
            { transform: 'translateX(0)' }
          ];

      animation = content.animate(keyframes, {
        duration,
        iterations: Infinity,
        easing: 'linear'
      });

      // Handle pause on hover
      if (config.pause_on_hover) {
        handleMouseEnter = () => {
          animation?.pause();
          setIsPaused(true);
        };
        handleMouseLeave = () => {
          animation?.play();
          setIsPaused(false);
        };

        container.addEventListener('mouseenter', handleMouseEnter);
        container.addEventListener('mouseleave', handleMouseLeave);
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      animation?.cancel();
      if (handleMouseEnter) container.removeEventListener('mouseenter', handleMouseEnter);
      if (handleMouseLeave) container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [config]);

  if (!config || !config.messages || config.messages.length === 0 || isAdminPage) return null;

  // Create the message string with separators
  const separator = config.show_separator ? config.separator_text : '     ';
  const singleMessage = config.messages.join(separator) + separator;

  // Repeat the message multiple times to ensure seamless loop without gaps
  // We need at least 2 copies for seamless animation, but more ensures no gaps on wide screens
  const repeatedMessage = singleMessage.repeat(10);

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden relative"
      style={{
        backgroundColor: config.background_color,
        height: config.height
      }}
      role="marquee"
      aria-live="off"
    >
      {/* Ticker content - duplicated for seamless loop */}
      <div
        ref={contentRef}
        className="flex items-center h-full whitespace-nowrap"
        style={{
          color: config.text_color,
          fontSize: config.font_size,
          fontWeight: 500
        }}
      >
        <span>{repeatedMessage}</span>
        <span>{repeatedMessage}</span>
      </div>

      {/* Pause indicator */}
      {isPaused && config.pause_on_hover && (
        <div
          className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2 py-1 rounded bg-black/20 text-xs"
          style={{ color: config.text_color }}
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
          </svg>
          PAUZA
        </div>
      )}
    </div>
  );
}
